/**
 * Every user-facing knob, with defaults tuned for the actual input this pipeline
 * sees: a screenshot-quality crop of a print-on-demand mockup, where the artwork
 * is flat vector-ish colour over fabric.
 */

export interface MatteSettings {
  /** Re-scale the network's raw output by its own min/max (RMBG reference behaviour). */
  normalize: boolean;
  /** Guided-filter window radius in source pixels. */
  guidedRadius: number;
  /** Guided-filter regularisation; lower hugs edges harder. */
  guidedEps: number;
  /** Solve the guided filter at 1/N resolution. */
  guidedSubsample: number;
  /** Signed morphology in pixels: negative shrinks the matte, positive grows it. */
  choke: number;
  /** Components smaller than this fraction of the frame are deleted. 0 disables. */
  minIslandFraction: number;
  /** Alpha levels remap. */
  low: number;
  high: number;
  gamma: number;
  gain: number;
  /** Colour decontamination strength, 0..1. */
  despill: number;
  /** Background colour for decontamination; `null` estimates it from the image. */
  background: [number, number, number] | null;
  /** How strongly brush overrides win over the network. */
  paintStrength: number;
}

export const DEFAULT_MATTE_SETTINGS: MatteSettings = {
  normalize: true,
  guidedRadius: 8,
  guidedEps: 1e-4,
  guidedSubsample: 4,
  choke: -1,
  minIslandFraction: 0.0004,
  low: 0.3,
  high: 0.7,
  gamma: 1,
  gain: 1,
  // Mockup garments leave a fringe on every semi-transparent edge pixel, so
  // decontamination is on by default - it is the difference between artwork that
  // survives being placed on a black tee and artwork that does not.
  despill: 0.8,
  background: null,
  paintStrength: 1,
};

export type AlphaUpscaleMode = 'guided' | 'model' | 'lanczos';

export interface UpscaleSettings {
  /** Network input tile edge, including context padding. */
  tileSize: number;
  /** Context padding per side, discarded from the model output. */
  overlap: number;
  /**
   * How the alpha channel is scaled.
   *
   * SR models are RGB-only. `guided` upsamples alpha with Lanczos and then snaps
   * it to the upscaled colour with a guided filter - nearly as good as running
   * the model twice, at a fraction of the cost. `model` runs the network on the
   * matte broadcast to RGB. `lanczos` is the cheap fallback.
   */
  alphaMode: AlphaUpscaleMode;
  /** Post-SR unsharp mask. */
  sharpenAmount: number;
  sharpenRadius: number;
  sharpenThreshold: number;
  /** Fill colour under transparent pixels before inference. */
  inpaintTransparent: boolean;
}

export const DEFAULT_UPSCALE_SETTINGS: UpscaleSettings = {
  tileSize: 256,
  overlap: 16,
  alphaMode: 'guided',
  sharpenAmount: 0.35,
  sharpenRadius: 2,
  sharpenThreshold: 0.02,
  inpaintTransparent: true,
};

export interface PaletteSettings {
  /** 0 disables quantisation. */
  colors: number;
  dither: number;
  linear: boolean;
}

export const DEFAULT_PALETTE_SETTINGS: PaletteSettings = {
  colors: 0,
  dither: 0,
  linear: true,
};
